import * as React from "react";
import { cn } from "src/lib/cn";

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {"base" | "xs" | "sm" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl"} [props.size="base"]
 * @param {"normal" | "wide" | "wider" | "widest" | "tighter" | "tight"} [props.letterSpacing="normal"]
 * @param {"none" | "tight" | "snug" | "normal" | "relaxed" | "loose" | "3" | "4" | "5" | "6" | "7" | "8"} [props.lineHeight="normal"]
 * @param {"left" | "center" | "right"} [props.align="left"]
 * @param {string} [props.as]
 * @param {"grey-0" | "grey-1" | "grey-2" | "body-color" | "menu-color" | "menu-color-hover" | "menu-color-active" | "menu-color-active-lightened" | "menu-color-transparent" | "primary-cta" | "primary-cta-high-contrast" | "primary-cta-transparent" | "danger-color" | "danger-color-hover" | "warning"} [props.color="grey-0"]
 * @param {"normal" | "medium" | "bold" | "extrabold" | "black" | "semibold"} [props.weight="normal"]
 * @param {"inter" | "quattro" | "opendyslexic" | "gt-super-text" | "gt-super-display" | "ibm-plex-mono" | "medieval-sharp"} [props.font="inter"]
 * @param {boolean} [props.truncate=false]
 * @param {"1" | "2" | "3" | "4" | "5" | "6" | "none"} [props.lineClamp="none"]
 * @param {React.HTMLAttributes<HTMLParagraphElement>} [props]
 * @returns {JSX.Element}
 * */
export const Text = ({
  children,
  className,
  size = "base",
  weight = "normal",

  truncate = false,
  lineClamp = "none",
  letterSpacing = "normal",
  lineHeight = "normal",
  align = "left",
  font = "inter",
  as,
  color = "grey-0",
  style,
  ...props
}) => {
  const Cmp = as || "p";

  const classes = cn(
    {
      "text-base": size === "base",
      "text-xs": size === "xs",
      "text-sm": size === "sm",
      "text-lg": size === "lg",
      "text-xl": size === "xl",
      "text-2xl": size === "2xl",
      "text-3xl": size === "3xl",
      "text-4xl": size === "4xl",
      "text-5xl": size === "5xl",
      "text-6xl": size === "6xl",

      "tracking-normal": letterSpacing === "normal",
      "tracking-wide": letterSpacing === "wide",
      "tracking-wider": letterSpacing === "wider",
      "tracking-widest": letterSpacing === "widest",
      "tracking-tighter": letterSpacing === "tighter",
      "tracking-tight": letterSpacing === "tight",

      "leading-none": lineHeight === "none",
      "leading-tight": lineHeight === "tight",
      "leading-snug": lineHeight === "snug",
      "leading-normal": lineHeight === "normal",
      "leading-relaxed": lineHeight === "relaxed",
      "leading-loose": lineHeight === "loose",
      "leading-3": lineHeight === "3",
      "leading-4": lineHeight === "4",
      "leading-5": lineHeight === "5",
      "leading-6": lineHeight === "6",
      "leading-7": lineHeight === "7",
      "leading-8": lineHeight === "8",

      "text-left": align === "left",
      "text-center": align === "center",
      "text-right": align === "right",

      "text-app-grey-0": color === "grey-0",
      "text-app-grey-1": color === "grey-1",
      "text-app-grey-2": color === "grey-2",
      "text-app-body-color": color === "body-color",
      "text-app-menu-color": color === "menu-color",
      "text-app-menu-color-hover": color === "menu-color-hover",
      "text-app-menu-color-active": color === "menu-color-active",
      "text-app-menu-color-active-lightened": color === "menu-color-active-lightened",
      "text-app-menu-color-transparent": color === "menu-color-transparent",
      "text-app-primary-cta-high-contrast": color === "primary-cta-high-contrast",
      "text-app-primary-cta": color === "primary-cta",
      "text-app-primary-cta-transparent": color === "primary-cta-transparent",
      "text-app-warning": color === "warning",
      "text-app-danger-color": color === "danger-color",
      "text-app-danger-color-hover": color === "danger-color-hover",
      "text-app-danger-color-active": color === "danger-color-active",

      "font-inter": font === "inter",
      "font-quattro": font === "quattro",
      "font-opendyslexic": font === "opendyslexic",
      "font-gt-super-text": font === "gt-super-text",
      "font-gt-super-display": font === "gt-super-display",
      "font-ibm-plex-mono": font === "ibm-plex-mono",
      "font-medieval-sharp": font === "medieval-sharp",

      "font-normal": weight === "normal",
      "font-medium": weight === "medium",
      "font-bold": weight === "bold",
      "font-extrabold": weight === "extrabold",
      "font-black": weight === "black",
      "font-semibold": weight === "semibold",

      "truncate text-ellipsis overflow-hidden": truncate,
      "line-clamp-1": lineClamp === "1",
      "line-clamp-2": lineClamp === "2",
      "line-clamp-3": lineClamp === "3",
      "line-clamp-4": lineClamp === "4",
      "line-clamp-5": lineClamp === "5",
      "line-clamp-6": lineClamp === "6",
      "line-clamp-none": lineClamp === "none",
    },
    className
  );

  return (
    <Cmp
      className={classes}
      style={{
        ...style,
      }}
      {...props}
    >
      {children}
    </Cmp>
  );
};
